import type { IGradeTarget, IGradeTargetPayload } from "@/@types/campaign-grade";
import type { IConflictResolution } from "@/@types/campaign-conflict";

export const CAMPAIGN_STATUS = {
  ACTIVE: "ACTIVE",
  FUTURE: "FUTURE",
  EXPIRED: "EXPIRED",
} as const;

export type CampaignStatusKey = keyof typeof CAMPAIGN_STATUS;
export type CampaignStatusValue = (typeof CAMPAIGN_STATUS)[CampaignStatusKey];

export enum CampaignVisualStatus {
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  REJECTED = 'REJECTED',
}

export interface ICampaign {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
  visualStatus: CampaignVisualStatus;
  dbStatus: number;
  storesCount: number;
  sla: string | null;
  createdBy: string;
  updatedAt: string;
  targetGroupId: number | null;
  discountTypeId: number | null;
  storeIndicator: number;
}

export interface ICampaignStore {
  code: number;
  name: string;
  startDate?: string;
  endDate?: string;
  visualStatus?: string;
}

export interface ICampaignProduct {
  sku: string;
  description: string;
  price: number;
}

export interface ICampaignHistoryEntry {
  action: string;
  user: string;
  timestamp: string;
  notes?: string;
}

export interface ICampaignDetails {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
  visualStatus: CampaignVisualStatus;
  createdBy: string;
  targetGroup: string | null;
  discountType: string | null;
  voucherValue: number | null;
  minimumPurchaseValue: number | null;
  validityDays: number | null;
  stores: ICampaignStore[];
  products: ICampaignProduct[];
  history: ICampaignHistoryEntry[];
  updatedAt: string;
}

export interface ICampaignParams {
  meta: {
    page: number;
    perPage: number;
  };
  search?: string;
  status?: CampaignVisualStatus[];
  startDateFrom?: string;
  startDateTo?: string;
  endDateFrom?: string;
  endDateTo?: string;
  stores?: number[];
  createdBy?: string;
  orderBy?: string;
}

export interface ICancelCampaignDto {
  reason?: string;
}

// ========================================
// UNIFIED CAMPAIGN TYPES (PRO012, PRO013, PRO014, PRO017)
// ========================================

export enum CampaignType {
  PRO012 = 'PRO012', // Combo Legado (Read-only)
  PRO013 = 'PRO013', // Leve X Pague Y
  PRO014 = 'PRO014', // Promoção com Itens
  PRO017 = 'PRO017', // Combo Moderno
}

export interface IUnifiedCampaign {
  type: CampaignType;
  code: number;
  description: string;
  storeCode: number;
  storeName: string;
  startDate: string;
  endDate: string;
  visualStatus: 'ACTIVE' | 'FUTURE' | 'EXPIRED';
  configSummary: string; // Type-specific summary
  canEdit: boolean;
  canDelete: boolean;
  storeCount?: number;
}

export interface IUnifiedCampaignParams {
  meta: {
    page: number;
    perPage: number;
  };
  campaignTypes?: CampaignType[];
  storeCode?: number;
  storeCodes?: number[];
  status?: ('ACTIVE' | 'FUTURE' | 'EXPIRED')[];
  startDateFrom?: string;
  startDateTo?: string;
  endDateFrom?: string;
  endDateTo?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: string;
  inEffect?: boolean;
  itemCode?: string;
  departmentCode?: string;
  campaignCode?: string;
}

export interface IUnifiedCampaignResponse {
  data: IUnifiedCampaign[];
  meta: {
    page: number;
    perPage: number;
    total: number;
    totalPages: number;
    countByType: {
      [CampaignType.PRO012]: number;
      [CampaignType.PRO013]: number;
      [CampaignType.PRO014]: number;
      [CampaignType.PRO017]: number;
    };
  };
}

// ========================================
// PRO013 SPECIFIC TYPES (Leve X Pague Y)
// ========================================

export interface IDepartment {
  code: number;
  name: string;
}

export interface IProductGroup {
  code: number;
  name: string;
}

export enum Pro013DiscountType {
  INTEGRAL = 0, // Desconto integral no item
  PERCENTAGE = 1, // Percentual sobre combo
  GLOBAL_STORE = 2, // Global na loja
  LOWEST_VALUE = 3, // Desconto no menor valor
  GLOBAL = 4, // Global na loja
}

export interface ICampaignPro013 {
  code: number;
  description: string;
  storeCode: number;
  storeName: string;
  startDate: string;
  endDate: string;
  takeQuantity: number;
  payQuantity: number;
  discountValue: number;
  discountType: Pro013DiscountType;
  allStoreFlag: number;
  paymentMethod: number;
  departments: IDepartment[];
  groups: IProductGroup[];
  visualStatus: 'ACTIVE' | 'FUTURE' | 'EXPIRED';
}

export interface ICreatePro013CampaignDto {
  description: string;
  storeCode: number | number[]; // 999 or array of store codes
  startDate: string;
  endDate: string;
  takeQuantity: number;
  payQuantity: number;
  discountValue: number;
  discountType: Pro013DiscountType;
  allStoreFlag: number;
  paymentMethod: number;
  departments: number[]; // Up to 15
  groups: number[]; // Up to 15
}

export interface IUpdatePro013CampaignDto {
  description?: string;
  startDate?: string;
  endDate?: string;
  takeQuantity?: number;
  payQuantity?: number;
  discountValue?: number;
  discountType?: Pro013DiscountType;
  departments?: number[];
  groups?: number[];
}

export interface NormalizedStore {
  code: number;
  name: string;
  state: string;
  city: string;
  regionalId: number | null;
}

export interface StoreSelectorValue {
  allStores: boolean;
  stores: number[];
}

export interface Pro014StepFourItem {
  sku: string;
  name?: string;
  grades?: IGradeTarget[];
}

export interface Pro014StepFourData {
  items: Pro014StepFourItem[];
  gradeRestricted: boolean;
}

export interface StorePreviewData {
  matchedCodes: number[];
  matchedStores: NormalizedStore[];
  alreadySelected: number[];
  notFound: string[];
}

export interface StoreMatchResult {
  matchedStores: NormalizedStore[];
  notFound: string[];
}

export const PRO014_STORE_MODE = {
  ALL: "ALL",
  SPECIFIC: "SPECIFIC",
  LEGACY: "LEGACY",
} as const;

export type Pro014StoreMode = (typeof PRO014_STORE_MODE)[keyof typeof PRO014_STORE_MODE];

export interface IPro014StoreWindow {
  storeCode: number;
  startDate: string;
  endDate: string;
  active: boolean;
}

export interface ICatalogItem {
  code: string;
  name: string;
}

export interface IPro014CampaignItemRef {
  code: string;
  isActive: boolean;
}

export interface IPro014CampaignDetail {
  code: number;
  description: string;
  startDate: string;
  endDate: string;
  takeQuantity: number;
  payQuantity: number;
  discountValue: number;
  discountType: number;
  paymentMethod: number;
  allStoreFlag: string | null;
  baseValue: number;
  status: CampaignStatusValue;
  storeMode: Pro014StoreMode;
  gradeRestricted: boolean;
  isMultiStore: boolean;
  stores: number[];
  storeWindows: IPro014StoreWindow[];
  items: IPro014CampaignItemRef[];
}

export type Pro014StoreScope = Pick<IPro014CampaignDetail, "storeMode" | "storeWindows">;

export interface IPro014CampaignItem {
  campaignCode: number;
  storeCode: number;
  itemCode: string;
  itemName: string;
  isActive: boolean;
  grades: IGradeTarget[];
}

export const PRO014_ITEM_STATUS_FILTER = {
  ALL: "all",
  ACTIVE: "active",
  INACTIVE: "inactive",
} as const;

export type Pro014ItemStatusFilter =
  (typeof PRO014_ITEM_STATUS_FILTER)[keyof typeof PRO014_ITEM_STATUS_FILTER];

export const PRO014_ITEM_SORT = {
  CODE_ASC: "code_asc",
  CODE_DESC: "code_desc",
  NAME_ASC: "name_asc",
  NAME_DESC: "name_desc",
} as const;

export type Pro014ItemSort = (typeof PRO014_ITEM_SORT)[keyof typeof PRO014_ITEM_SORT];

export const PRO014_ITEMS_MAX_LIMIT = 100;

export interface IPro014ItemsPage {
  items: IPro014CampaignItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface IPro014ItemsPageParams {
  code: number;
  page?: number;
  limit?: number;
  status?: Pro014ItemStatusFilter;
  search?: string;
  sort?: Pro014ItemSort;
  storeCode?: number;
}

export interface IPro014EditSnapshot {
  detail: IPro014CampaignDetail;
  items: IPro014CampaignItem[];
}

export interface IPro014ItemsSummary {
  stats: {
    total: number;
    active: number;
    inactive: number;
  };
  items: Array<{
    itemCode: string;
    itemName: string;
    isActive: boolean;
  }>;
}

export interface IPro014QueuedJob {
  queued: true;
  jobId: string | number;
  itemsCount: number;
  statusUrl: string;
}

export interface ICreatePro014CampaignRequest {
  description: string;
  startDate: string;
  endDate: string;
  storeCode?: number;
  stores?: number[];
  takeQuantity: number;
  payQuantity: number;
  discountValue: number;
  discountType: number;
  paymentMethod: number;
  baseValue?: number;
  items: string[];
  gradeRestricted?: boolean;
  gradeTargets?: IGradeTargetPayload[];
  conflictResolutions?: IConflictResolution[];
}

export interface ICreatePro014CampaignResponse {
  message: string;
  data: { code: number; recordsCreated: number; itemsCreated: number } | IPro014QueuedJob;
}

export interface IPro014CreationJobStatus {
  id: string | number;
  state: string;
  progress?: unknown;
  result?: unknown;
  data?: {
    campaignType: string;
    description?: string;
    itemsCount: number;
    identifierRequest?: string;
  };
  failedReason?: string;
}

export interface IUpdatePro014CampaignRequest {
  code: number;
  description?: string;
  startDate?: string;
  endDate?: string;
  takeQuantity?: number;
  payQuantity?: number;
  discountValue?: number;
  discountType?: number;
  baseValue?: number;
}

export interface IUpdatePro014CampaignResponse {
  message: string;
  data: { code: number; recordsUpdated: number };
}

export interface IUpdatePro014StoresBody {
  allStores: boolean;
  stores?: number[];
}

export interface IUpdatePro014StoresRequest extends IUpdatePro014StoresBody {
  code: number;
}

export interface IUpdatePro014StoresApplied {
  code: number;
  storeMode: Pro014StoreMode;
  added: number[];
  reopened: number[];
  reset: number[];
  ended: number[];
}

export interface IUpdatePro014StoresLegacyApplied {
  code: number;
  added: number[];
  removed: number[];
}

export interface IUpdatePro014StoresNoop {
  code: number;
  noop: true;
  storeMode?: Pro014StoreMode;
  added?: number[];
  removed?: number[];
}

export interface IUpdatePro014StoresQueued {
  code: number;
  added: number[];
  removed: number[];
  queued: true;
  jobId: string | number;
  statusUrl: string;
}

export type Pro014StoresUpdateResult =
  | IUpdatePro014StoresApplied
  | IUpdatePro014StoresLegacyApplied
  | IUpdatePro014StoresNoop
  | IUpdatePro014StoresQueued;

export interface IUpdatePro014StoresResponse {
  message: string;
  data: Pro014StoresUpdateResult;
}

export interface IUpdatePro014GradeRestrictionRequest {
  code: number;
  enabled: boolean;
}

export interface IUpdatePro014GradeRestrictionResponse {
  message: string;
  data: { code: number; gradeRestricted: boolean };
}

export interface IAddPro014ItemsRequest {
  code: number;
  itemCodes: string[];
  gradeTargets?: IGradeTargetPayload[];
}

export interface IAddPro014ItemsResult {
  code: number;
  itemsAdded: number;
  skippedItemCodes: string[];
  reactivatedItemCodes: string[];
}

export interface IAddPro014ItemsResponse {
  message: string;
  data: IAddPro014ItemsResult;
}

export interface IPro014ItemRef {
  code: number;
  itemCode: string;
}

export interface IPro014StoreItemRef extends IPro014ItemRef {
  storeCode: number;
}

export const PRO014_BULK_ACTION = {
  ACTIVATE: "activate",
  DEACTIVATE: "deactivate",
} as const;

export type Pro014BulkAction = (typeof PRO014_BULK_ACTION)[keyof typeof PRO014_BULK_ACTION];

export interface IBulkUpdatePro014ItemsRequest {
  code: number;
  items: Array<{ itemCode: string; storeCode: number }>;
  action: Pro014BulkAction;
}

export interface IBulkUpdatePro014ItemsResponse {
  message: string;
  affected: number;
}

export interface IPro014Message {
  message: string;
}

export interface IUnifiedCampaignCodeRef {
  code: number | string;
}

export interface IUnifiedCampaignList {
  listNumber: number;
  items?: IUnifiedCampaignCodeRef[];
}

export interface IEndCampaignTarget {
  campaignCode: number;
  campaignType: CampaignType;
  startDate: string;
  currentEndDate: string;
}

export interface IEndDateLimits {
  today: string;
  startDay: string;
  currentEndDay: string;
}

export interface IEndCampaignFormValues {
  endDate: string;
}
